import {
	Code,
	FileUp, // Import FileUp
	KeyRound,
	Link as LinkIcon,
	QrCode,
	RotateCw,
	Truck,
} from "lucide-react";
import type React from "react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";

interface ToolCardProps {
	title: string;
	description: string;
	href: string;
	id: string;
	icon?: React.ElementType; // Allow passing icon as prop
}

// Icon-Mapping für Tool-IDs mit gemeinsamer Farbpalette
const getIconForTool = (id: string, propIcon?: React.ElementType) => {
	// Prefer prop icon if provided
	if (propIcon) {
		const IconComponent = propIcon;
		return (
			<IconComponent className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
		);
	}

	switch (id) {
		case "bulletpoints":
			return (
				<Truck className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
			);
		case "totp":
			return (
				<RotateCw className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
			);
		case "passwort":
			return (
				<KeyRound className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
			);
		case "qrcode":
			return (
				<QrCode className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
			);
		case "link-share":
			return (
				<LinkIcon className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
			);
		case "file-share": // Added case for file-share
			return (
				<FileUp className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
			);
		default:
			return (
				<Code className="mb-2 h-10 w-10 text-indigo-500 transition-colors group-hover:text-indigo-600 dark:text-indigo-400 dark:group-hover:text-indigo-300" />
			);
	}
};

export default function ToolCard({
	title,
	description,
	href,
	id,
	icon: propIcon,
}: ToolCardProps) {
	const icon = getIconForTool(id, propIcon);

	return (
		<Link href={href}>
			<Card className="group relative h-full cursor-pointer overflow-hidden border bg-white/90 backdrop-blur-sm transition-all duration-300 hover:scale-102 hover:border-indigo-200 hover:shadow-md dark:border-gray-700 dark:bg-gray-800/90 dark:hover:border-indigo-800 dark:hover:shadow-xl">
				{/* Hintergrund-Highlight */}
				<div className="absolute inset-0 bg-linear-to-br from-indigo-50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-indigo-950/50 dark:to-transparent" />

				<CardContent className="relative flex min-h-28 items-center justify-center gap-2 p-4 text-center">
					<div className="flex flex-col items-center justify-center">
						{icon}
						<h2 className="mb-1 line-clamp-2 font-semibold text-base text-gray-800 transition-colors group-hover:text-indigo-700 dark:text-gray-100 dark:group-hover:text-indigo-400">
							{title}
						</h2>
						<p className="line-clamp-2 text-gray-600 text-xs transition-colors dark:text-gray-300">
							{description}
						</p>
					</div>
				</CardContent>
			</Card>
		</Link>
	);
}
